import {I18n} from '@aws-amplify/core';
import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {ReportType} from '../types';

// FNP-918: was `tables/columns.tsx` — a bare array where EVERY column was
// `render`-only, with no `accessor` on any of them.
//
// That is the distinction that made this module unsortable. `render` controls
// how a cell LOOKS; `accessor` is what sorting sorts BY. A render-only config
// looks complete in review and makes sorting structurally impossible. Moved
// into a hook to match the platform shape (checklist §3z).
//
// ⚠️ `updated`, `dataAvailableStart` and `dataAvailableEnd` are UNIX EPOCH
// SECONDS (typed `number`), not AWSDateTime strings. The old config cast
// `item.updated as unknown as string` and handed raw SECONDS to
// formatTimestamp, which moment parses as MILLISECONDS — so every row rendered
// as a date in January 1970. The cast silenced the very type error that was
// pointing at the bug. Conversion now happens in useReportTypes, so display
// and sort share one normalised value.
export const useReportTypesTableColumns = () => {

    const columns: DataTableColumn<ReportType>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            width: '2fr',
            // `name` is nullable on the API; falls back to the id so the row is
            // never a blank link.
            render: (item: ReportType) => (
                <NavLink to={`/report-types/${encodeURIComponent(item.id)}`}>
                    {item.name || item.id}
                </NavLink>
            ),
        },
        {
            accessor: 'id',
            sortable: true,
            title: I18n.get('Report Type'),
            width: '2fr',
            // The id IS the identifier an operator quotes when requesting a
            // run ("balance.summary.1"), so it earns its own column rather
            // than only backing the name. Monospace, as a long id (§13).
            render: (item: ReportType) => (
                <span style={{fontFamily: 'monospace'}}>{item.id}</span>
            ),
        },
        {
            accessor: 'version',
            sortable: true,
            title: I18n.get('Version'),
            width: '1fr',
            // Numeric, so it sorts numerically rather than putting 10 before 9
            // — which is what a string version column would do (§3a).
            render: (item: ReportType) => item.version ?? '—',
        },
        {
            accessor: 'dataAvailableStartMillis',
            sortable: true,
            title: I18n.get('Data From'),
            width: '1fr',
            // The window this report can actually cover. An operator picking an
            // interval for a run needs it, and it was not surfaced anywhere
            // before — the old table showed only Name/Version/Updated.
            render: (item: ReportType) =>
                formatTimestamp(item.dataAvailableStartMillis),
        },
        {
            accessor: 'dataAvailableEndMillis',
            sortable: true,
            title: I18n.get('Data To'),
            width: '1fr',
            render: (item: ReportType) =>
                formatTimestamp(item.dataAvailableEndMillis),
        },
        {
            accessor: 'updatedMillis',
            sortable: true,
            title: I18n.get('Updated'),
            width: '1fr',
            render: (item: ReportType) => formatTimestamp(item.updatedMillis),
        },
    ];

    return {columns};
};
